import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Event, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

/** Arquivo com hash de conteúdo no nome (ng build --output-hashing=all), ex.: main.0123456789abcdef.js. */
const HASHED_ASSET = /(?:^|\/)([\w-]+\.[0-9a-f]{16,}\.(?:js|css))(?:[?#]|$)/i;
const ASSET_REF = /\b(?:src|href)\s*=\s*["']([^"']+)["']/gi;

/** Nomes dos bundles com hash entre as referências (só locais; CDN e afins ficam de fora). */
export function hashedAssets(refs: string[]): string[] {
  const names = new Set<string>();
  for (const ref of refs) {
    if (!ref || ref.includes('://') || ref.startsWith('//')) {
      continue;
    }
    const match = HASHED_ASSET.exec(ref);
    if (match) {
      names.add(match[1]);
    }
  }
  return Array.from(names);
}

/** src/href citados num HTML (o index.html do servidor). */
export function assetRefs(html: string): string[] {
  const refs: string[] = [];
  const pattern = new RegExp(ASSET_REF.source, ASSET_REF.flags);
  let match: RegExpExecArray;
  while ((match = pattern.exec(html)) !== null) {
    refs.push(match[1]);
  }
  return refs;
}

/** Chunk lazy da versão anterior que o deploy apagou do servidor (webpack). */
export function isChunkLoadError(error: any): boolean {
  if (!error) {
    return false;
  }
  return error.name === 'ChunkLoadError' || /Loading (?:CSS )?chunk [^\s]+ failed/i.test(String(error.message));
}

/**
 * Tudo o que o AppUpdateService faz no navegador: ler o index.html do servidor, listar os bundles desta
 * página, recarregar e guardar a hora da última recarga automática. Separado para o teste simular.
 */
@Injectable({
  providedIn: 'root',
})
export class AppUpdateBrowser {
  static readonly LAST_RELOAD_KEY = 'easyAppUpdateLastReload';

  /** index.html atual do servidor, sem passar pelo cache do navegador; null se não respondeu 200. */
  async fetchIndexHtml(): Promise<string | null> {
    const url = new URL('index.html', document.baseURI);
    url.searchParams.set('v', String(Date.now()));
    const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
    return response.ok ? response.text() : null;
  }

  /** src/href dos scripts e folhas de estilo carregados nesta página. */
  loadedAssetRefs(): string[] {
    return Array.from(document.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
      .map((element) => element.getAttribute('src') || element.getAttribute('href'));
  }

  /**
   * Recarrega a página. Com destino, a recarga já abre nele: o endereço entra no histórico como numa
   * navegação normal (ou substitui o atual) e o reload busca o index novo.
   */
  reload(externalUrl?: string, replaceUrl = false): void {
    if (externalUrl) {
      const target = new URL(externalUrl, window.location.href).href;
      if (target !== window.location.href) {
        if (replaceUrl) {
          window.history.replaceState(null, '', target);
        } else {
          window.history.pushState(null, '', target);
        }
      }
    }
    window.location.reload();
  }

  now(): number {
    return Date.now();
  }

  lastReload(): number {
    try {
      return Number(sessionStorage.getItem(AppUpdateBrowser.LAST_RELOAD_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  saveLastReload(time: number): void {
    try {
      sessionStorage.setItem(AppUpdateBrowser.LAST_RELOAD_KEY, String(time));
    } catch {
      // sem sessionStorage (modo privado restrito): segue sem a trava
    }
  }
}

/**
 * Leva cada deploy do www2 a quem está com a tela aberta, sem limpar cache e sem modal.
 *
 * O www2 não tem service worker: a aba roda a versão que carregou até a página ser recarregada.
 * - Ao iniciar e a cada CHECK_INTERVAL_MS, baixa o index.html do servidor (sem cache) e compara os bundles
 *   com hash que ele cita (main, runtime, polyfills, vendor, scripts, styles) com os desta página.
 *   Se mudou, marca "recarregar na próxima navegação".
 * - No próximo NavigationStart a página recarrega já no destino. Ninguém perde formulário preenchido.
 *   Navegação com skipLocationChange (ex.: volta técnica à raiz para recriar a tela) espera a seguinte.
 * - Se um chunk lazy da versão antiga sumiu com o deploy (ChunkLoadError), recarrega na hora no destino.
 * - Trava contra laço: no máximo uma recarga automática a cada RELOAD_MIN_INTERVAL_MS por aba.
 * Dentro do iframe do front novo vale o mesmo: cada janela confere e recarrega só a si mesma.
 * Sem bundles com hash (ng serve, teste) fica desligado.
 */
@Injectable({
  providedIn: 'root',
})
export class AppUpdateService implements OnDestroy {
  static readonly CHECK_INTERVAL_MS = 10 * 60 * 1000;
  static readonly RELOAD_MIN_INTERVAL_MS = 10 * 60 * 1000;

  private initialized = false;
  private reloadOnNextNavigation = false;
  private interval: any;
  private routerSubscription: Subscription;

  constructor(
    private router: Router,
    private location: Location,
    private zone: NgZone,
    private browser: AppUpdateBrowser,
  ) {}

  get isEnabled(): boolean {
    return hashedAssets(this.browser.loadedAssetRefs()).length > 0;
  }

  get pendingReload(): boolean {
    return this.reloadOnNextNavigation;
  }

  init(): void {
    if (this.initialized || !this.isEnabled) {
      return;
    }
    this.initialized = true;

    this.routerSubscription = this.router.events.subscribe((event) => this.onRouterEvent(event));
    // fora da zona do Angular: a conferência não dispara detecção de mudança nem segura o whenStable
    this.zone.runOutsideAngular(() => {
      this.checkForUpdate();
      this.interval = setInterval(() => this.checkForUpdate(), AppUpdateService.CHECK_INTERVAL_MS);
    });
  }

  /** Confere se o servidor já tem outra versão; se tiver, marca a recarga na próxima navegação. */
  async checkForUpdate(): Promise<boolean> {
    if (this.reloadOnNextNavigation) {
      return true;
    }
    let html: string | null;
    try {
      html = await this.browser.fetchIndexHtml();
    } catch {
      return false;
    }
    if (!html) {
      return false;
    }
    const served = hashedAssets(assetRefs(html));
    const loaded = new Set(hashedAssets(this.browser.loadedAssetRefs()));
    const changed = served.length > 0 && served.some((asset) => !loaded.has(asset));
    if (changed && this.canReload()) {
      this.reloadOnNextNavigation = true;
    }
    return this.reloadOnNextNavigation;
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
    this.routerSubscription?.unsubscribe();
  }

  private onRouterEvent(event: Event): void {
    if (event instanceof NavigationStart && this.reloadOnNextNavigation) {
      const extras = this.router.getCurrentNavigation()?.extras;
      if (extras?.skipLocationChange) {
        return;
      }
      this.reloadAt(event.url, !!extras?.replaceUrl);
    } else if (event instanceof NavigationError && isChunkLoadError(event.error) && this.canReload()) {
      this.reloadAt(event.url, false);
    }
  }

  private reloadAt(url: string, replaceUrl: boolean): void {
    this.reloadOnNextNavigation = false;
    this.browser.saveLastReload(this.browser.now());
    this.browser.reload(this.location.prepareExternalUrl(url), replaceUrl);
  }

  private canReload(): boolean {
    return this.browser.now() - this.browser.lastReload() >= AppUpdateService.RELOAD_MIN_INTERVAL_MS;
  }
}
